import React, { useState, useEffect } from 'react';
import { Box, Alert, Skeleton } from '@mui/material';
import { WalteroCard, WalteroTypography, WalteroButton, WalteroDataTable } from '../waltero';
import { subscriptionService } from '../../services/subscriptionService';
import CreateSubscriptionTypeModal from './CreateSubscriptionTypeModal';
import EditSubscriptionTypeModal from './EditSubscriptionTypeModal';

const SubscriptionTypes = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState(null);
  const [organizationSubscriptionCounts, setOrganizationSubscriptionCounts] = useState(new Map());

  useEffect(() => {
    loadSubscriptionTypes();
  }, []);

  const loadSubscriptionTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const [typesResult, orgSubscriptionsResult] = await Promise.all([
        subscriptionService.getSubscriptionTypes(),
        subscriptionService.getAllOrganizationSubscriptions()
      ]);

      const types = typesResult.data || [];
      const organizationSubscriptions = orgSubscriptionsResult.data || [];

      const countsMap = new Map();
      organizationSubscriptions.forEach((subscription) => {
        if (!subscription?.subscriptionTypeId) {
          return;
        }
        const typeId = subscription.subscriptionTypeId;
        countsMap.set(typeId, (countsMap.get(typeId) || 0) + 1);
      });

      setOrganizationSubscriptionCounts(new Map(countsMap));
      setSubscriptionTypes(types);
    } catch (err) {
      console.error('Error loading subscription types:', err);
      setError(err.message || 'Failed to load subscription types');
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (event, row, index) => {
    console.log('🔍 Row clicked:', row);
    console.log('🔍 Row data type:', typeof row);
    console.log('🔍 Row keys:', Object.keys(row));
    setSelectedSubscriptionType(row);
    setEditModalOpen(true);
  };

  const columns = [
    {
      id: 'name',
      label: 'Name',
      render: (value) => value || 'N/A'
    },
    {
      id: 'description',
      label: 'Description',
      render: (value) => value || 'No description'
    },
    {
      id: 'durationDays',
      label: 'Duration (Days)',
      align: 'center',
      render: (value) => value || 0
    },
    {
      id: 'basePrice',
      label: 'Base Price (per sensor / month)',
      align: 'right',
      render: (value) => value ? `$${value}` : '-'
    },
    {
      id: 'gracePeriodDays',
      label: 'Grace Period (Days)',
      align: 'center',
      render: (value) => (value ?? 0)
    },
    {
      id: 'isActive',
      label: 'Status',
      align: 'center',
      render: (value) => (
        <Box
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: value ? 'success.light' : 'error.light',
            color: value ? 'success.dark' : 'error.dark',
            textAlign: 'center',
            fontSize: '0.75rem'
          }}
        >
          {value ? 'Active' : 'Inactive'}
        </Box>
      )
    },
    {
      id: 'organizationCount',
      label: 'Organizations',
      align: 'center',
      render: (_, row) => organizationSubscriptionCounts.get(row.id) || 0
    },
  ];

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        <strong>Error Loading Subscription Types</strong>
        <br />
        {error}
        <Box sx={{ mt: 2 }}>
          <WalteroButton 
            variant="outlined" 
            onClick={loadSubscriptionTypes}
            size="small"
          >
            RETRY
          </WalteroButton>
        </Box>
      </Alert>
    );
  }

  return (
    <Box>
      <WalteroCard>
        <Box sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Box>
              <WalteroTypography 
                variant="h5" 
                sx={{ 
                  color: 'waltero.primary.coreBlue',
                  fontWeight: 600,
                  mb: 1
                }}
              >
                Subscription Types
              </WalteroTypography>
              <WalteroTypography 
                variant="body2" 
                sx={{ color: 'text.secondary' }}
              >
                Manage global subscription templates that can be assigned to organizations.
              </WalteroTypography>
            </Box>
            <WalteroButton 
              variant="contained" 
              onClick={() => setCreateModalOpen(true)}
            >
              CREATE TYPE
            </WalteroButton>
          </Box>

          {loading ? (
            <Box>
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} height={60} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : subscriptionTypes.length === 0 ? (
            <Alert severity="info">
              No subscription types defined yet. Create your first type to get started.
            </Alert>
          ) : (
            <WalteroDataTable
              data={subscriptionTypes}
              columns={columns}
              loading={loading}
              title=""
              showTitle={false}
              filterable={false}
              pagination={false}
              showControls={false}
              onRowClick={handleRowClick}
            />
          )}
        </Box>
      </WalteroCard>

      <CreateSubscriptionTypeModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => {
          loadSubscriptionTypes(); // Refresh the data
        }}
      />

      <EditSubscriptionTypeModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedSubscriptionType(null);
        }}
        onSuccess={() => {
          loadSubscriptionTypes(); // Refresh the data
        }}
        subscriptionType={selectedSubscriptionType}
      />
    </Box>
  );
};

export default SubscriptionTypes;
